import api from './api';

const bookingService = {
  /**
   * Process booking email content using OpenAI API
   * @param {string} emailContent - The raw email content to process
   * @returns {Promise<Object>} - Processed booking data and database IDs
   */
  processBookingEmail: async (emailContent) => {
    try {
      const response = await api.post('/v1/booking/process-email-ai/', {
        email_content: emailContent
      });
      return response.data;
    } catch (error) {
      console.error('Error processing booking email:', error);
      throw error;
    }
  },

  /**
   * Get all bookings with pagination
   * @param {Object} params - Query parameters (page, page_size, status, search)
   * @returns {Promise<Object>} - Paginated bookings list
   */
  getBookings: async (params = {}) => {
    try {
      const response = await api.get('/v1/bookings/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw error;
    }
  },

  /**
   * Get a specific booking by ID
   * @param {number} bookingId - The booking ID
   * @returns {Promise<Object>} - Booking details
   */
  getBookingById: async (bookingId) => {
    try {
      const response = await api.get(`/v1/bookings/${bookingId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking:', error);
      throw error;
    }
  },

  /**
   * Update booking status
   * @param {number} bookingId - The booking ID
   * @param {string} status - New status
   * @returns {Promise<Object>} - Updated booking
   */
  updateBookingStatus: async (bookingId, status) => {
    try {
      const response = await api.patch(`/bookings/${bookingId}/`, {
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  },

  /**
   * Get advisories for a specific booking
   * @param {number} bookingId - The booking ID
   * @returns {Promise<Array>} - List of advisories
   */
  getBookingAdvisories: async (bookingId) => {
    try {
      const response = await api.get(`/bookings/${bookingId}/advisories/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking advisories:', error);
      throw error;
    }
  },

  /**
   * Create a manual booking
   * @param {Object} bookingData - The booking data
   * @returns {Promise<Object>} - Created booking details
   */
  createManualBooking: async (bookingData) => {
    try {
      const response = await api.post('/v1/booking/create-manual/', bookingData);
      return response.data;
    } catch (error) {
      console.error('Error creating manual booking:', error);
      throw error;
    }
  },

  /**
   * Update an existing booking
   * @param {number} bookingId - The booking ID
   * @param {Object} bookingData - The updated booking data
   * @returns {Promise<Object>} - Updated booking details
   */
  updateBooking: async (bookingId, bookingData) => {
    try {
      const response = await api.patch(`/v1/bookings/${bookingId}/update/`, bookingData);
      return response.data;
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    }
  },

  /**
   * Delete a booking
   * @param {number} bookingId - The booking ID to delete
   * @returns {Promise<Object>} - Deletion confirmation
   */
  deleteBooking: async (bookingId) => {
    try {
      const response = await api.delete(`/v1/bookings/${bookingId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  }
};

export default bookingService;